import { PawPrint, Truck, MapPin, Heart, Star, ShieldCheck, Package, Clock, ArrowRight, Facebook, Instagram, Twitter } from "lucide-react";

// Bespoke pitch demo for Mid-South Puppy Delivery — "Uber for puppy delivery" + dog food.
// Front page + menu only — ALL links/buttons decorative. No TraceElements/Emergent branding.
// Hero art is the client's own logo; supporting photos curated (client site was unreachable).

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/1c3ba24b8fcde872_a64ae83b-0567-4cff-b4c5-904e0ef39a25.jpg";

const PHOTOS = {
  passenger1: "https://images.unsplash.com/photo-1629740067905-bd3f515aa739?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjdXRlJTIwcHVwcHklMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTMzODYwNDJ8MA&ixlib=rb-4.1.0&q=85",
  passenger2: "https://images.unsplash.com/photo-1599692392256-2d084495fe15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxjdXRlJTIwcHVwcHklMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTMzODYwNDJ8MA&ixlib=rb-4.1.0&q=85",
  passenger3: "https://images.unsplash.com/photo-1526660690293-bcd32dc3b123?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxjdXRlJTIwcHVwcHklMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTMzODYwNDJ8MA&ixlib=rb-4.1.0&q=85",
  handoff: "https://images.unsplash.com/photo-1548858806-e064cf9872c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjBob2xkaW5nJTIwcHVwcHl8ZW58MHx8fHwxNzkzMzg2MDQyfDA&ixlib=rb-4.1.0&q=85",
  concierge: "https://images.unsplash.com/photo-1569339235754-14228772f415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjBob2xkaW5nJTIwcHVwcHl8ZW58MHx8fHwxNzkzMzg2MDQyfDA&ixlib=rb-4.1.0&q=85",
  dogFood: "https://images.unsplash.com/photo-1714068691210-073dc52c6c1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxkb2clMjBmb29kJTIwYm93bHxlbnwwfHx8fDE3OTMzODYwNDF8MA&ixlib=rb-4.1.0&q=85",
};

const BLUE = "#1D2E7A";
const RED = "#E01F2E";
const GOLD2 = "#F7B32B";
const CREAM = "#FFF9F0";

const MENU = ["Home", "Delivery", "Puppies", "Dog Food", "Pricing", "About", "Contact"];

const STEPS = [
  { icon: PawPrint, title: "1 · Book in 2 Minutes", desc: "Tell us who's riding, from where, to where. Instant quote — no phone tag, no paperwork pileup." },
  { icon: Truck, title: "2 · Meet Your Driver", desc: "A certified, background-checked pet driver accepts the trip. Climate-controlled, crate-comfy, camera-checked." },
  { icon: MapPin, title: "3 · Track Them Home", desc: "Live GPS from pickup to doorstep. Photo updates at every stop. Tails wag the whole way." },
];

const PASSENGERS = [
  { name: "Biscuit", route: "Memphis → Nashville", breed: "French Bulldog", img: PHOTOS.passenger1 },
  { name: "Waffles", route: "Jackson → Collierville", breed: "Golden Retriever", img: PHOTOS.passenger2 },
  { name: "Maple", route: "Oxford → Germantown", breed: "Corgi", img: PHOTOS.passenger3 },
];

const noop = (e) => e.preventDefault();

export default function MidsouthPuppyDemo() {
  return (
    <div className="min-h-screen font-jakarta" style={{ backgroundColor: CREAM, color: "#221E1A" }} data-testid="puppy-demo">
      {/* ===== TOP BAR ===== */}
      <div className="text-white text-center py-2 px-4 text-[11px] font-extrabold uppercase tracking-[0.22em]" style={{ backgroundColor: RED }} data-testid="puppy-top-bar">
        Fast · Safe · Reliable — Puppies delivered safe &amp; with love
      </div>

      {/* ===== HEADER + MENU (decorative) ===== */}
      <header className="sticky top-0 z-50 backdrop-blur-lg border-b border-black/5" style={{ backgroundColor: "rgba(255,249,240,0.92)" }}>
        <div className="max-w-7xl mx-auto px-6 h-[76px] flex items-center justify-between gap-6">
          <a href="#" onClick={noop} className="flex items-center gap-3" data-testid="puppy-logo">
            <img src={LOGO} alt="Mid-South Puppy Delivery logo" className="h-11 w-11 rounded-full object-cover border-2" style={{ borderColor: GOLD2 }} />
            <span className="font-extrabold text-lg tracking-tight leading-none">
              Mid-South <span style={{ color: RED }}>Puppy Delivery</span>
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-7 text-[13px] font-bold" data-testid="puppy-menu">
            {MENU.map((item) => (
              <a key={item} href="#" onClick={noop} className="text-[#221E1A]/75 hover:text-[#E01F2E] transition-colors" data-testid={`puppy-menu-${item.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {item}
              </a>
            ))}
          </nav>
          <a href="#" onClick={noop} className="hidden sm:inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] font-extrabold uppercase tracking-[0.18em] text-white transition-transform hover:-translate-y-0.5" style={{ backgroundColor: RED, boxShadow: "0 8px 24px rgba(224,31,46,0.35)" }} data-testid="puppy-book-header">
            <Truck size={14} /> Book a Delivery
          </a>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden text-white" style={{ backgroundColor: BLUE }} data-testid="puppy-hero">
        <PawPrint className="absolute top-16 left-[8%] opacity-10 rotate-[-20deg]" size={90} />
        <PawPrint className="absolute bottom-20 right-[12%] opacity-10 rotate-[25deg]" size={70} />
        <PawPrint className="absolute top-1/2 left-[45%] opacity-[0.07] rotate-[10deg]" size={120} />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-[0.22em]" style={{ backgroundColor: GOLD2, color: BLUE }} data-testid="puppy-hero-badge">
              <Truck size={13} /> The Uber for Puppy Delivery
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-extrabold tracking-tight leading-[1.04]" data-testid="puppy-hero-headline">
              Puppies Delivered <span style={{ color: GOLD2 }}>Safe &amp; With Love.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-white/75 max-w-xl" data-testid="puppy-hero-subheadline">
              On-demand puppy transport across the Mid-South — with live GPS tracking, certified pet drivers,
              and fresh dog food delivered on repeat. Your best friend's ride, handled like family.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#" onClick={noop} className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5" style={{ backgroundColor: RED, boxShadow: "0 12px 32px rgba(224,31,46,0.4)" }} data-testid="puppy-hero-cta-book">
                Book a Puppy Delivery <ArrowRight size={15} />
              </a>
              <a href="#" onClick={noop} className="inline-flex items-center gap-2 border-2 border-white/30 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] hover:border-[#F7B32B] hover:text-[#F7B32B] transition-colors" data-testid="puppy-hero-cta-food">
                Shop Dog Food
              </a>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-bold text-white/60">
              <span className="inline-flex items-center gap-1.5"><Truck size={13} style={{ color: GOLD2 }} /> Fast</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} style={{ color: GOLD2 }} /> Safe</span>
              <span className="inline-flex items-center gap-1.5"><Heart size={13} style={{ color: GOLD2 }} /> Reliable</span>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-[520px]">
              <div className="absolute -inset-4 rounded-[40px] opacity-30 blur-2xl" style={{ backgroundColor: RED }} />
              <img src={LOGO} alt="Mid-South Puppy Delivery — puppies delivered safe and with love" className="relative w-full rounded-[32px] shadow-2xl border-4 border-white/15" data-testid="puppy-hero-image" />
              <div className="absolute -left-4 lg:-left-8 -bottom-6 bg-white text-[#221E1A] rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
                <span className="h-10 w-10 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: RED }}>
                  <MapPin size={16} />
                </span>
                <div>
                  <div className="text-sm font-extrabold">Live Tracking</div>
                  <div className="text-xs text-[#221E1A]/55">Every mile, every tail wag</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS RIBBON ===== */}
      <section style={{ backgroundColor: GOLD2 }} className="border-y-4" data-testid="puppy-stats">
        <div className="max-w-7xl mx-auto px-6 py-9 grid grid-cols-2 lg:grid-cols-4 gap-8" style={{ borderColor: BLUE }}>
          {[
            { stat: "500+", label: "Puppies Home Safe" },
            { stat: "98%", label: "On-Time Arrivals" },
            { stat: "24/7", label: "Live Trip Tracking" },
            { stat: "5.0", label: "Average Rating" },
          ].map((m) => (
            <div key={m.label} className="text-center" data-testid={`puppy-stat-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: BLUE }}>{m.stat}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.22em] font-extrabold" style={{ color: `${BLUE}99` }}>{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-16 lg:py-24" data-testid="puppy-how">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em]" style={{ color: RED }}>How It Works</div>
            <h2 className="mt-3 text-3xl lg:text-5xl font-extrabold tracking-tight" style={{ color: BLUE }}>Easy as a Belly Rub.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="bg-white rounded-3xl border border-black/5 p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`puppy-step-${i}`}>
                  <span className="h-14 w-14 rounded-2xl flex items-center justify-center text-white" style={{ backgroundColor: i === 1 ? RED : BLUE }}>
                    <Icon size={24} />
                  </span>
                  <h3 className="mt-6 text-xl font-extrabold tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#221E1A]/60">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="py-16 lg:py-24 bg-white border-y border-black/5" data-testid="puppy-services">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.25em]" style={{ color: RED }}>What We Do</div>
              <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight" style={{ color: BLUE }}>Three Ways We Spoil Your Pup</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { title: "Puppy Delivery", desc: "Door-to-door puppy transport across Tennessee, Mississippi and Arkansas. Solo rides or shared routes.", img: PHOTOS.handoff, testid: "puppy-service-delivery" },
              { title: "Dog Food Delivery", desc: "Premium kibble and fresh blends on a schedule you set. Never lug a 40-pound bag again.", img: PHOTOS.dogFood, testid: "puppy-service-food" },
              { title: "Puppy Concierge", desc: "Vet runs, grooming pickups, daycare shuttles. If your pup needs to be somewhere, we drive.", img: PHOTOS.concierge, testid: "puppy-service-concierge" },
            ].map((s) => (
              <a key={s.title} href="#" onClick={noop} className="group block" data-testid={s.testid}>
                <div className="overflow-hidden rounded-3xl">
                  <img src={s.img} alt={s.title} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="mt-5 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-extrabold text-lg tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#221E1A]/60">{s.desc}</p>
                  </div>
                  <span className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white transition-transform group-hover:-translate-y-1" style={{ backgroundColor: GOLD2 }}>
                    <ArrowRight size={16} style={{ color: BLUE }} />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HAPPY PASSENGERS ===== */}
      <section className="py-16 lg:py-24" data-testid="puppy-passengers">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em]" style={{ color: RED }}>Recent Trips</div>
            <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight" style={{ color: BLUE }}>Happy Passengers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {PASSENGERS.map((p, i) => (
              <div key={p.name} className="bg-white rounded-3xl border border-black/5 overflow-hidden shadow-sm hover:shadow-xl transition-shadow" data-testid={`puppy-passenger-${i}`}>
                <img src={p.img} alt={`${p.name} the ${p.breed}`} loading="lazy" className="w-full aspect-square object-cover" />
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="font-extrabold text-lg tracking-tight">{p.name}</div>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, s) => <Star key={s} size={12} style={{ color: GOLD2 }} fill="currentColor" />)}
                    </div>
                  </div>
                  <div className="mt-1 text-xs font-bold uppercase tracking-[0.15em]" style={{ color: RED }}>{p.breed}</div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#221E1A]/55 font-semibold">
                    <MapPin size={12} style={{ color: BLUE }} /> {p.route}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DOG FOOD CLUB BANNER ===== */}
      <section className="py-16 lg:py-20 px-6" data-testid="puppy-food-club">
        <div className="max-w-7xl mx-auto relative overflow-hidden rounded-[36px] text-white" style={{ backgroundColor: BLUE }}>
          <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-25 hidden lg:block">
            <img src={PHOTOS.dogFood} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1D2E7A] via-[#1D2E7A]/85 to-transparent" />
          <div className="relative px-8 lg:px-16 py-14 lg:py-20 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-[0.22em]" style={{ backgroundColor: GOLD2, color: BLUE }}>
              <Package size={13} /> The Dog Food Club
            </div>
            <h2 className="mt-5 text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight" data-testid="puppy-food-heading">
              Fresh Dog Food,<br />Delivered on Repeat.
            </h2>
            <p className="mt-4 text-sm lg:text-base leading-relaxed text-white/70">
              Premium kibble and fresh-blend plans from <span className="font-extrabold" style={{ color: GOLD2 }}>$34/bag</span> —
              auto-delivered with your pup's next ride or on its own route. Skip, pause or swap anytime.
            </p>
            <a href="#" onClick={noop} className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5" style={{ backgroundColor: RED }} data-testid="puppy-food-cta">
              Start a Food Plan <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIAL ===== */}
      <section className="py-16 lg:py-24" data-testid="puppy-testimonial">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-1">
            {[...Array(5)].map((_, s) => <Star key={s} size={20} style={{ color: GOLD2 }} fill="currentColor" />)}
          </div>
          <blockquote className="mt-6 text-2xl lg:text-3xl font-extrabold tracking-tight leading-snug" style={{ color: BLUE }} data-testid="puppy-testimonial-quote">
            "Our Frenchie arrived calm, happy, and right on time — and we tracked him the whole way home.
            It's literally Uber, but for puppies."
          </blockquote>
          <div className="mt-6 text-sm font-bold text-[#221E1A]/60">— The Harrison Family, Memphis TN</div>
          <div className="mt-10 flex items-center justify-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#221E1A]/40">
            <Clock size={13} /> Booked in 2 minutes · Delivered same day
          </div>
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section className="py-16 px-6" data-testid="puppy-cta-band">
        <div className="max-w-7xl mx-auto rounded-[36px] px-8 lg:px-16 py-14 text-center text-white relative overflow-hidden" style={{ backgroundColor: RED }}>
          <PawPrint className="absolute -top-6 -left-6 opacity-15 rotate-[-15deg]" size={120} />
          <PawPrint className="absolute -bottom-8 -right-4 opacity-15 rotate-[20deg]" size={140} />
          <h2 className="relative text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="puppy-cta-heading">Ready to Roll?</h2>
          <p className="relative mt-4 text-sm lg:text-base text-white/80 max-w-xl mx-auto">
            Book your puppy's ride in two minutes flat — or set up their food plan while you're at it.
          </p>
          <a href="#" onClick={noop} className="relative mt-8 inline-flex items-center gap-2 bg-white px-9 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5" style={{ color: RED }} data-testid="puppy-cta-button">
            Book a Puppy Delivery <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* ===== FOOTER (decorative) ===== */}
      <footer className="text-white" style={{ backgroundColor: BLUE }} data-testid="puppy-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Mid-South Puppy Delivery logo" className="h-10 w-10 rounded-full object-cover border-2" style={{ borderColor: GOLD2 }} />
              <span className="font-extrabold tracking-tight">Mid-South <span style={{ color: GOLD2 }}>Puppy Delivery</span></span>
            </div>
            <p className="mt-4 text-sm text-white/60 leading-relaxed">
              On-demand puppy transport and dog food delivery across the Mid-South. Fast. Safe. Reliable.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {[Facebook, Instagram, Twitter].map((Icon, i) => (
                <a key={i} href="#" onClick={noop} aria-label="Social link" className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#F7B32B] hover:text-[#F7B32B] transition-colors" data-testid={`puppy-social-${i}`}>
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/40 mb-4">Services</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {["Puppy Delivery", "Dog Food Plans", "Puppy Concierge", "Vet Runs", "Daycare Shuttle"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#F7B32B] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/40 mb-4">Company</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {["About Us", "Our Drivers", "Safety Standards", "Careers", "Press"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#F7B32B] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/40 mb-4">Service Area</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li className="flex items-center gap-2"><MapPin size={13} style={{ color: GOLD2 }} /> Memphis, TN</li>
              {["North Mississippi", "East Arkansas", "Nashville Runs"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#F7B32B] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-white/45">© 2026 Mid-South Puppy Delivery. All rights reserved.</span>
            <span className="text-xs text-white/45 inline-flex items-center gap-1.5"><PawPrint size={12} style={{ color: GOLD2 }} /> Puppies delivered safe &amp; with love.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
